/**
 * Nut Sheet API service.
 *
 * Wraps the four /nut-sheet endpoints with the same token-injection pattern
 * shared by api.ts + projectionsApi.ts. Token getter is set by AuthContext.
 */

import { ApiResponse } from '../types';
import {
  ListEntriesRangeResponse,
  ListEntriesResponse,
  NutSheetEntry,
  SaveEntryRequest,
  DealLookupResponse,
} from '../types/nutSheet';

const API_URL = process.env.REACT_APP_API_URL || '';

let getAccessTokenFn: ((forceRefresh?: boolean) => Promise<string | null>) | null = null;

export function setTokenGetter(fn: (forceRefresh?: boolean) => Promise<string | null>) {
  getAccessTokenFn = fn;
}

let isRefreshingToken = false;
let tokenRefreshPromise: Promise<string | null> | null = null;

async function forceRefreshToken(): Promise<string | null> {
  if (isRefreshingToken && tokenRefreshPromise) return tokenRefreshPromise;
  isRefreshingToken = true;
  tokenRefreshPromise = (async () => {
    try {
      if (getAccessTokenFn) return await getAccessTokenFn(true);
      return null;
    } finally {
      isRefreshingToken = false;
      tokenRefreshPromise = null;
    }
  })();
  return tokenRefreshPromise;
}

async function apiRequest<T>(
  endpoint: string,
  options: RequestInit = {},
  isRetry: boolean = false,
): Promise<ApiResponse<T>> {
  try {
    let authHeaders: Record<string, string> = {};
    if (getAccessTokenFn) {
      const token = await getAccessTokenFn();
      if (token) authHeaders['Authorization'] = `Bearer ${token}`;
    }

    const response = await fetch(`${API_URL}${endpoint}`, {
      ...options,
      headers: {
        'Content-Type': 'application/json',
        ...authHeaders,
        ...options.headers,
      },
    });

    const data = await response.json();

    if (!response.ok) {
      if ((response.status === 401 || response.status === 403) && !isRetry) {
        const newToken = await forceRefreshToken();
        if (newToken) return apiRequest<T>(endpoint, options, true);
      }
      // 409 = optimistic-concurrency conflict. Don't throw — the body carries
      // { success:false, conflict:true, data:<current server copy> } so the
      // caller can reconcile instead of surfacing a generic error.
      if (response.status === 409) {
        return data as ApiResponse<T>;
      }
      throw new Error(data.error || `HTTP error ${response.status}`);
    }
    return data;
  } catch (error: any) {
    console.error(`Nut Sheet API request failed: ${endpoint}`, error);
    return { success: false, error: error.message || 'Network error occurred' };
  }
}

export function lookupDealNumber(
  locationId: number,
  dealNumber: string,
  date?: string,
): Promise<ApiResponse<DealLookupResponse>> {
  // Route kept as /stock-lookup (renaming it would burn CFN resources); the
  // dealNumber param selects the deal-driven lookup on the backend.
  let qs = `?locationId=${locationId}&dealNumber=${encodeURIComponent(dealNumber)}`;
  // `date` scopes the duplicate-entry check to that month's partition.
  if (date) qs += `&date=${encodeURIComponent(date)}`;
  return apiRequest<DealLookupResponse>(`/nut-sheet/stock-lookup${qs}`);
}

export function listNutSheetEntries(
  locationId: number,
  date: string,
  excludeFiReserve?: boolean,
): Promise<ApiResponse<ListEntriesResponse>> {
  // `excludeFiReserve` makes the server compute the MTD F&I summary net of each
  // deal's finance reserve (some pay plans pay on F&I minus reserve).
  let qs = `?locationId=${locationId}&date=${encodeURIComponent(date)}`;
  if (excludeFiReserve) qs += `&excludeFiReserve=true`;
  return apiRequest<ListEntriesResponse>(`/nut-sheet/entries${qs}`);
}

export function listNutSheetEntriesRange(
  locationId: number,
  startDate: string,
  endDate: string,
  excludeFiReserve?: boolean,
): Promise<ApiResponse<ListEntriesRangeResponse>> {
  // Range mode of the same /nut-sheet/entries route (Export to Excel): returns
  // every entry in [startDate, endDate] plus a summary over exactly that range.
  let qs = `?locationId=${locationId}&startDate=${encodeURIComponent(startDate)}&endDate=${encodeURIComponent(endDate)}`;
  if (excludeFiReserve) qs += `&excludeFiReserve=true`;
  return apiRequest<ListEntriesRangeResponse>(`/nut-sheet/entries${qs}`);
}

export function saveNutSheetEntry(
  entry: SaveEntryRequest,
): Promise<ApiResponse<NutSheetEntry>> {
  // `expectedVersion` rides inside the SaveEntryRequest body; the server uses
  // it for the optimistic-concurrency check. On a 409 the response carries
  // `conflict: true` + `data` = the current server copy.
  return apiRequest<NutSheetEntry>('/nut-sheet/entries', {
    method: 'POST',
    body: JSON.stringify(entry),
  });
}

export function deleteNutSheetEntry(
  locationId: number,
  date: string,
  entryId: string,
  expectedVersion?: number | null,
): Promise<ApiResponse<NutSheetEntry>> {
  let qs = `?locationId=${locationId}&date=${encodeURIComponent(date)}&entryId=${encodeURIComponent(entryId)}`;
  if (typeof expectedVersion === 'number') {
    qs += `&expectedVersion=${expectedVersion}`;
  }
  return apiRequest<NutSheetEntry>(`/nut-sheet/entries${qs}`, {
    method: 'DELETE',
  });
}
